import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { atomFamily } from 'jotai-family';
import { IAnnouncement } from '../RidePlayer/types';
import { IGraphResponseData } from '../Graphs/GraphService';
import { TVehicleStatus } from '../Vehicles/types';
import { OnlinePanelData } from '../OnlineMapNew/PanelsLayer/types';

export interface OnlinePanelDefaultTemplateChange {
  customerId: number | null;
  template: OnlinePanelData | null;
  vehicleId: number[] | null;
}

export interface OnlinePanelUserTemplateChange {
  customerId: number | null;
  template: OnlinePanelData | null;
  userId: number | null;
  vehicleId: number[] | null;
}

export interface ActiveWaypointItem {
  id: number;
  name: string;
  type: string;
  userId: number;
}

export interface IVehicleAlarmItem {
  id: number;
  name: string;
  users: number[];
}

export interface IWidgetGraph {
  vehicleId: number;
  timestamp?: string;
  data?: IGraphResponseData[];
  name?: string;
  rideId?: string;
}

export interface IVehicleOnlinePanelElectric {
  charging: boolean | null;
  state: boolean | null;
  plugged: boolean | null;
  percent: number | null;
  remaining: number | null;
  batteryRange: number | null;
}

export interface IVehicleOnlinePanelState {
  adBlueLevel: number | null;
  vehicleId: number;
  altitude: number | null;
  battery: number | null;
  engineTemperature: number | null;
  engineState: null | boolean;
  fuelLevelLiters: number | null;
  fuelLevelPercentage: number | null;
  rpm: number | null;
  satellites: number | null;
  speed: number | null;
  throttle: number | null;
  ecoDriveScore: number | null;
  timestamp: string;
  onCruise: boolean | null;
  onCng: boolean | null;
  onLpg: boolean | null;
  remoteLock: boolean | null;
  latitude: number;
  longitude: number;
  announcements: IAnnouncement[] | [];
  address: string | null;
  webasto: null | boolean;
  pto: null | boolean;
  ignition: boolean;
  electric: IVehicleOnlinePanelElectric | null;
  code: string;
  mileage: null | {
    timestamp: string;
    value: number;
  };
  seatbelts: {
    driver: boolean;
    frontPassenger: boolean;
    leftPassenger: boolean;
    rightPassenger: boolean;
    centrePassenger: boolean;
  } | null;
  coolantLevelLow: null | boolean;
  oilLevelLow: null | boolean;
  checkEngine: null | boolean;
  esp: boolean | null;
}

export const ANNOUNCEMENT_TYPE_EVENT = 'event';
export const ANNOUNCEMENT_TYPE_WAYPOINT = 'waypoint';
export const ANNOUNCEMENT_TYPE_ALARM = 'alarm';
export const FRONT_LEFT_DOOR_OPENED = 'front_left_door_opened';
export const FRONT_RIGHT_DOOR_OPENED = 'front_right_door_opened';
export const REAR_LEFT_DOOR_OPENED = 'rear_left_door_opened';
export const REAR_RIGHT_DOOR_OPENED = 'rear_right_door_opened';
export const HOOD_OPENED = 'hood_opened';
export const TRUNK_OPENED = 'trunk_opened';
export const ALL_DOORS_OPENED = 'all_doors_opened';
export const WARNING_LIGHT_ON = 'warning_light_on';
export const WARNING_LIGHT_OFF = 'warning_light_off';
export const WARNING_SOUND_ON = 'warning_sound_on';
export const WARNING_SOUND_OFF = 'warning_sound_off';
export const ANNOUNCEMENT_TYPE_EVENT_BINARY = 'binary';
export const ANNOUNCEMENT_TYPE_EVENT_DOORS = 'door';
export const ANNOUNCEMENT_TYPE_EVENT_BLUECOINS = 'bluecoin';
export const ANNOUNCEMENT_TYPE_EVENT_INPUT = 'input';

export interface IVehicleOnlinePanels {
  [vehicleId: number]: IVehicleOnlinePanelState;
}

// Atoms
export const isOnlinePanelOpenAtom = atom<number | false>(false);
export type OnlinePanelDebugMode = 'navigator' | 'legacy' | 'navigatorInline';
// Debug-only runtime switch to test different online panel implementations.
// - navigator: show `NavigatorOnlinePanel` in sidebar (slides out to the left of the navigator)
// - navigatorInline: show `NavigatorInlineOnlinePanel` (replaces the navigator in place)
// - legacy: show legacy map `OnlineMapNew/PanelsLayer/OnlinePanel`
export const onlinePanelDebugModeAtom = atom<OnlinePanelDebugMode>('legacy');
export const onlinePanelAtom = atom<IVehicleOnlinePanels>({});
export const onlineMapTemplatesAtom = atom<OnlinePanelData[]>([]);
export const defaultOnlinePanelTemplateAtom = atom<OnlinePanelDefaultTemplateChange>({
  customerId: null,
  template: null,
  vehicleId: null,
});
export const userOnlinePanelTemplateAtom = atom<OnlinePanelUserTemplateChange>({
  customerId: null,
  template: null,
  userId: null,
  vehicleId: null,
});
export const isLoadingExtraDataForWidgetsAtom = atom<boolean>(false);
export const lastStatusAtom = atom<TVehicleStatus>('STOP');

// Responsive behaviour of the map online panel when it does not fit the available width:
// - scale:  keep the designed layout, shrink it proportionally (transform: scale)
// - reflow: re-pack widgets into fewer columns (more rows)
export type OnlinePanelResponsiveMode = 'scale' | 'reflow';
export const onlinePanelResponsiveModeAtom = atomWithStorage<OnlinePanelResponsiveMode>(
  'onlinePanelResponsiveMode',
  'scale'
);
export const onlinePanelDataAtom = atom<OnlinePanelData | null>(null);

// Derived: online panel for a given vehicle id
export const onlinePanelForVehicleAtom = atomFamily((vehicleId: number | false) =>
  atom((get) => {
    if (!vehicleId) return null;
    const data = get(onlinePanelAtom);
    return data[vehicleId] ?? null;
  })
);

// Derived: online panel for currently selected vehicle (isOnlinePanelOpen)
export const onlinePanelForSelectedVehicleAtom = atom((get) => {
  const vehicleId = get(isOnlinePanelOpenAtom);
  if (!vehicleId) return null;
  const data = get(onlinePanelAtom);
  return data[vehicleId] ?? null;
});

// Derived: binary events for a vehicle
export const binaryEventsAtom = atomFamily((vehicleId: number | false) =>
  atom((get) => {
    if (!vehicleId) return [];
    const data = get(onlinePanelAtom);
    const panel = data[vehicleId];
    if (!panel) return [];
    return panel.announcements.filter(
      (dataItem: IAnnouncement) =>
        dataItem.type === ANNOUNCEMENT_TYPE_EVENT &&
        (dataItem.eventType === ANNOUNCEMENT_TYPE_EVENT_BINARY ||
          dataItem.eventType === ANNOUNCEMENT_TYPE_EVENT_INPUT)
    );
  })
);

// Derived: door events for a vehicle
export const doorsEventsForVehicleAtom = atomFamily((vehicleId: number | false) =>
  atom((get) => {
    if (!vehicleId) return [];
    const data = get(onlinePanelAtom);
    const panel = data[vehicleId];
    if (!panel) return [];
    return panel.announcements.filter(
      (dataItem: IAnnouncement) =>
        dataItem.type === ANNOUNCEMENT_TYPE_EVENT &&
        (dataItem.title === FRONT_LEFT_DOOR_OPENED ||
          dataItem.title === FRONT_RIGHT_DOOR_OPENED ||
          dataItem.title === REAR_LEFT_DOOR_OPENED ||
          dataItem.title === REAR_RIGHT_DOOR_OPENED ||
          dataItem.title === HOOD_OPENED ||
          dataItem.title === TRUNK_OPENED ||
          dataItem.title === ALL_DOORS_OPENED)
    );
  })
);

// Derived: door events for selected vehicle
export const doorsEventsForSelectedVehicleAtom = atom((get) => {
  const vehicleId = get(isOnlinePanelOpenAtom);
  if (!vehicleId) return [];
  const data = get(onlinePanelAtom);
  const panel = data[vehicleId];
  if (!panel) return [];
  return panel.announcements.filter(
    (dataItem: IAnnouncement) =>
      dataItem.type === ANNOUNCEMENT_TYPE_EVENT &&
      (dataItem.title === FRONT_LEFT_DOOR_OPENED ||
        dataItem.title === FRONT_RIGHT_DOOR_OPENED ||
        dataItem.title === REAR_LEFT_DOOR_OPENED ||
        dataItem.title === REAR_RIGHT_DOOR_OPENED ||
        dataItem.title === HOOD_OPENED ||
        dataItem.title === TRUNK_OPENED ||
        dataItem.title === ALL_DOORS_OPENED)
  );
});
